/* eslint-disable jsx-a11y/anchor-has-content */
// The Above&Beyond report, shared by both layouts.
//
// This is the advanced report shown when the questionnaire has advanced = 1 and
// the AB tab is selected. It is the same report in V1 and V2 - the layout was
// lifted out of ReportV1 unchanged so the two variants cannot drift apart.
//
// It keeps its own gold V1-era styling (ReportContent with ab={true} over the
// pre-rendered /assets/images/report/ab/<lang>/slide-*.jpg backgrounds) rather
// than the V2 design system, because the AB report is a separate deck with its
// own branding.
//
// Data loading, saving and the AI calls stay in ReportBase; this file is layout
// only and receives everything it needs as props. Each theme's "what you do
// best" slides offer AI highlights for that theme, as the category slides of
// the regular report do (the A&B report has no suggestions slides).
import React from "react";
import {
  BarChart,
  Bar,
  LabelList,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  XAxis,
  YAxis,
} from "recharts";

import {
  ReportAbHighlights,
  ReportBenchmark,
  ReportContent,
  ReportCustomBenchmark,
} from "../../components/report";
import { ReportCircleChart } from "../../components/report/components";
import ReportLegend from "../../components/report/ReportLegend";
import ReportFullInput from "../../components/report/ReportFullInput";
import {
  abAbout,
  abAboutCerifiers,
  abMethodology,
  abAboutCertificate,
  abEpMethodology,
  abEvaluation,
  abExecSummary,
  abCategories,
} from "../../components/report/components/ReportTemplates";
import { getLanguage } from "../../utils/utils";
import { abCategoryDescription } from "./ReportBase";

// A category only earns its slides once it reaches this score.
const CATEGORY_THRESHOLD = 70;

export default function ReportAboveBeyond({
  content,
  data,
  editable,
  messages,
  onContentChange,
  aiItems = {},
  aiLoading = {},
  onGetAi,
}) {
  const language = getLanguage();
  const slide = (name) => `/assets/images/report/ab/${language}/${name}.jpg`;

  // The categories strong enough to get their own pages, used both for the
  // list on the intro slide and for the per-category group further down.
  const topCategories = data.aboveBeyond.filter(
    (item) => item.percentage >= CATEGORY_THRESHOLD
  );

  return (
    <>
      <ReportContent ab={true} zoomed={true} background={slide("slide-01")}>
        <div style={{ padding: "0 0 0 0" }}>
          <h1
            style={{
              fontFamily: "DmSansBold",
              fontSize: "1.8rem",
              margin: "70px 0 20px 0",
            }}
          >
            {messages.abCoverIntro}
            <br />
            <span style={{ color: "#dfaf56" }}>{data.info.name}</span>
            <br />
            {messages.abCoverOutro}
          </h1>
          <p
            style={{ maxWidth: "380px" }}
            dangerouslySetInnerHTML={{ __html: messages.abCoverInfo }}
          />
          <p>
            <strong>
              {messages.abCoverCertificated}
              {` `}
              <span
                style={{ color: "#dfaf56" }}
                onInput={(event) =>
                  onContentChange({
                    target: {
                      name: "abReportDate",
                      value: event.currentTarget.textContent,
                    },
                  })
                }
                contentEditable={editable}
                suppressContentEditableWarning={true}
              >
                {content.abReportDate ? content.abReportDate : data.info.year}
              </span>
            </strong>
          </p>
        </div>
      </ReportContent>

      <ReportContent ab={true} hideBadge={true} background={slide("slide-02")}>
        <div dangerouslySetInnerHTML={{ __html: abAbout[language] }} />
      </ReportContent>
      <ReportContent ab={true} hideBadge={true} background={slide("slide-03")}>
        <div dangerouslySetInnerHTML={{ __html: abAboutCerifiers[language] }} />
      </ReportContent>
      <ReportContent ab={true} hideBadge={true} background={slide("slide-04")}>
        <div dangerouslySetInnerHTML={{ __html: abMethodology[language] }} />
      </ReportContent>
      <ReportContent ab={true} background={slide("slide-05")}>
        <div
          dangerouslySetInnerHTML={{ __html: abAboutCertificate[language] }}
        />
      </ReportContent>
      <ReportContent ab={true} background={slide("slide-06")}></ReportContent>
      <ReportContent ab={true} background={slide("slide-07")}>
        <div dangerouslySetInnerHTML={{ __html: abEpMethodology[language] }} />
      </ReportContent>
      <ReportContent ab={true} background={slide("slide-08")}></ReportContent>
      <ReportContent ab={true} background={slide("slide-09")}>
        <div dangerouslySetInnerHTML={{ __html: abEvaluation[language] }} />
      </ReportContent>
      <ReportContent ab={true} background={slide("slide-10")}>
        <div dangerouslySetInnerHTML={{ __html: abExecSummary[language] }} />
      </ReportContent>

      <ReportContent ab={true} background={slide("slide-11")}>
        <div dangerouslySetInnerHTML={{ __html: abCategories[language] }} />
        <ul
          style={{
            color: "#dfaf56",
            left: "445px",
            position: "absolute",
            top: "345px",
          }}
        >
          {topCategories.map((item) => (
            <li key={item.category}>
              <strong>{item.category}</strong>
            </li>
          ))}
        </ul>
      </ReportContent>

      <ReportContent additionalClass="report__ab__results" ab={true}>
        <h2
          style={{ fontSize: "1.6rem", margin: "40px 0 0 0" }}
          dangerouslySetInnerHTML={{ __html: messages.abResultsTitle }}
        />
        <div
          style={{
            alignItems: "center",
            display: "flex",
            paddingBottom: "40px",
          }}
        >
          <div>
            <ReportCircleChart
              value={data.result.overal}
              previousValue={data.result.average}
              size={180}
              stroke={30}
              gold={true}
            />
            <ReportLegend
              first={messages.yourOrganization}
              second={messages.employerPartnerAvg}
            />
          </div>
          <div
            className="report__ab__info"
            dangerouslySetInnerHTML={{ __html: messages.abResultsInfo }}
          />
          <div className="report__ab__chart">
            <BarChart
              margin={{ top: 15, right: 5, bottom: 5, left: 5 }}
              width={400}
              height={400}
              data={topCategories}
            >
              <Bar name={data.info.year} dataKey="percentage" fill="#f9d877">
                <LabelList position="top" formatter={(value) => `${value}%`} />
              </Bar>
              <XAxis
                type="category"
                dataKey="category"
                width={200}
                interval={0}
                height={140}
              />
              <YAxis type="number" domain={[0, 100]} />
            </BarChart>
          </div>
        </div>
      </ReportContent>

      <ReportContent ab={true}>
        <div style={{ display: "flex" }}>
          <div style={{ padding: "60px 0 0 0" }}>
            <h2
              style={{ fontSize: "1.6rem" }}
              dangerouslySetInnerHTML={{
                __html: messages.abResultsCategoriesTitle,
              }}
            />
            <p
              style={{ maxWidth: "240px" }}
              dangerouslySetInnerHTML={{
                __html: messages.abResultsCategoriesInfo,
              }}
            />
          </div>
          <div>
            <RadarChart
              outerRadius={150}
              width={500}
              height={500}
              data={data.aboveBeyond}
              fontSize={11}
            >
              <PolarGrid />
              <PolarAngleAxis dataKey="categoryWithValue" />
              <PolarRadiusAxis fontSize={9} angle={90} domain={[0, 100]} />
              <Radar
                dataKey="percentage"
                stroke="#b9762c"
                fill="#f9d877"
                fillOpacity={0.6}
              />
            </RadarChart>
          </div>
        </div>
      </ReportContent>

      {(data.abBenchmarkSlides || []).length > 0 ? (
        // The benchmark slides of the questionnaire's type, from the database
        // (reportAbBenchmark): percentages as bars, other figures with units.
        data.abBenchmarkSlides.map((slide, index) =>
          slide.layout === "figures" ? (
            <ReportContent
              key={slide.slide}
              ab={true}
              id={index === 0 ? "page-ab-benchmark" : undefined}
              title={messages.abHighlightedNumbers}
            >
              <ReportCustomBenchmark
                benchmarks={slide.items}
                labelAverage={messages.aboveBeyondAvg}
              />
            </ReportContent>
          ) : (
            <ReportBenchmark
              key={slide.slide}
              ab={true}
              benchmarkLabel={messages.aboveBeyondAvg}
              id={index === 0 ? "page-ab-benchmark" : undefined}
              title={messages.abHighlightedNumbers}
              messages={messages}
              data={slide.items}
            />
          ),
        )
      ) : (
        // Type 1: the fixed questions of the original A&B report.
        <>
          <ReportBenchmark
            ab={true}
            benchmarkLabel={messages.aboveBeyondAvg}
            id="page-ab-benchmark"
            title={messages.abHighlightedNumbers}
            messages={messages}
            data={data.abBenchmarksPercentage.slice(0, 7)}
          />

          <ReportBenchmark
            ab={true}
            benchmarkLabel={messages.aboveBeyondAvg}
            title={messages.abHighlightedNumbers}
            messages={messages}
            data={data.abBenchmarksPercentage.slice(7)}
          />

          <ReportContent ab={true} title={messages.abHighlightedNumbers}>
            <ReportCustomBenchmark
              benchmarks={data.abBenchmarks}
              labelAverage={messages.aboveBeyondAvg}
            />
          </ReportContent>
        </>
      )}

      {topCategories.map((item) => {
        const suggestions = data.abHighlights.filter(
          (highlight) =>
            highlight.category === item.category && highlight.grade === 4
        );
        const title = `<span style="color:#dfaf56">${item.category}:</span> ${messages.abWhatYouDoBest}`;
        // Both slides of the theme share its AI highlights.
        const ai = [
          {
            type: "highlights",
            heading: messages.aiHighlightsHeading,
            buttonLabel: messages.getAiHighlights,
            items: aiItems[`ab_highlights_${item.category}`],
            loading: aiLoading[`ab_highlights_${item.category}`],
            onGet: onGetAi ? () => onGetAi(item.category, "highlights") : null,
          },
        ];

        return (
          <React.Fragment key={item.category}>
            <ReportContent
              ab={true}
              id="page-ab-steps"
              background={slide("slide-category")}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    abCategoryDescription[item.category.toLowerCase()][
                      language
                    ],
                }}
              />
            </ReportContent>
            <ReportAbHighlights
              id="page-ab-highlights"
              background={slide("slide-background")}
              title={title}
              onChange={onContentChange}
              name={`abHighLights${item.category}`}
              value={content[`abHighLights${item.category}`]}
              suggestions={suggestions}
              editable={editable}
              ai={ai}
            />
            <ReportAbHighlights
              id="page-ab-highlights-additional"
              additionalClass={
                content[`abHighLightsAdditional${item.category}`]
                  ? null
                  : "report__page__wrapper--empty"
              }
              background={slide("slide-background")}
              title={title}
              onChange={onContentChange}
              name={`abHighLightsAdditional${item.category}`}
              value={content[`abHighLightsAdditional${item.category}`]}
              suggestions={suggestions}
              editable={editable}
              ai={ai}
            />
          </React.Fragment>
        );
      })}

      <ReportFullInput
        editable={editable}
        ab={true}
        name="wordsFromTeam"
        title={messages.abWordsFromCertificationTeam}
        defaultValue={content.wordsFromTeam}
        onChange={onContentChange}
      />

      <ReportContent ab={true} background={slide("slide-29")}></ReportContent>
      <ReportContent ab={true} background={slide("slide-30")}></ReportContent>
      <ReportContent ab={true} background={slide("slide-final")}>
        <>
          <a
            href="https://www.linkedin.com/company/employer-partner/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              height: "20px",
              left: "110px",
              position: "absolute",
              top: "242px",
              width: "130px",
            }}
          ></a>
          <a
            href="mailto:contact@employerpartner.eu"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              height: "20px",
              left: "110px",
              position: "absolute",
              top: "277px",
              width: "190px",
            }}
          ></a>
          <a
            href="https://selectio.hr/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              height: "20px",
              left: "100px",
              position: "absolute",
              top: "400px",
              width: "100px",
            }}
          ></a>
          <a
            href="https://employerpartner.eu/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "block",
              height: "20px",
              left: "180px",
              position: "absolute",
              top: "400px",
              width: "130px",
            }}
          ></a>
        </>
      </ReportContent>
    </>
  );
}
